// src/features/tools/components/ToolProfileModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Modal, 
  Button, 
  Tabs, 
  Tab, 
  Card, 
  Row, 
  Col, 
  Form, 
  Badge, 
  Spinner, 
  Alert,
  ListGroup 
} from 'react-bootstrap';
import { 
  FaTimes, 
  FaPlus, 
  FaCheckSquare, 
  FaHistory, 
  FaTools, 
  FaCalendarAlt,
  FaUser,
  FaClipboardCheck,
  FaBriefcase
} from 'react-icons/fa';
import dayjs from 'dayjs';

// Servicios
import ToolsService from '../ToolsService';
import InspectionsService from '../../inspections/InspectionsService';

// Componentes
import CustomSnackbar from '../../../components/CustomSnackbar';
import TableWrapper from '../../../components/TableWrapper';

// Constantes y utilidades
import { 
  TIPOS_EQUIPOS, 
  TIPOS_INSPECCIONES_EQUIPOS, 
  getTipoDescripcion,
  FORMATO_FECHA 
} from '../../../utils/constants';

/**
 * Modal reutilizable para mostrar el perfil completo de una herramienta asignada
 * Puede ser usado desde diferentes módulos (personas, obras, etc.)
 * 
 * @param {boolean} show - Controla la visibilidad del modal
 * @param {function} onHide - Función para cerrar el modal
 * @param {string} toolId - ID de la herramienta asignada
 * @param {function} onToolUpdate - Callback cuando se actualiza la herramienta (opcional)
 * @param {function} showSnackbar - Función para mostrar notificaciones (opcional)
 * @param {string} contextModule - Módulo desde donde se llama ('people', 'works', etc.) para personalización
 */
const ToolProfileModal = ({ 
  show, 
  onHide, 
  toolId, 
  onToolUpdate, 
  showSnackbar,
  contextModule = 'general'
}) => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [toolData, setToolData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });

  // Estados para el formulario de inspección
  const [inspectionForm, setInspectionForm] = useState({
    type: '',
    date: dayjs().format('YYYY-MM-DD'),
    responsable: '',
    observations: ''
  });
  const [submittingInspection, setSubmittingInspection] = useState(false);

  // Refs para control
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);

  // Control de montaje
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (show && toolId && mountedRef.current) {
      loadToolData();
      // Resetear formulario cuando se abre el modal
      resetInspectionForm();
    }
  }, [show, toolId]);

  // Función para resetear el formulario de inspección
  const resetInspectionForm = () => {
    setInspectionForm({
      type: '',
      date: dayjs().format('YYYY-MM-DD'),
      responsable: '',
      observations: ''
    });
  };

  // Función para cargar datos de la herramienta asignada
  const loadToolData = useCallback(async () => {
    if (loadingRef.current || !toolId) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      
      //console.log(`🔄 [${contextModule}] Cargando datos de herramienta asignada ID: ${toolId}`);
      
      const response = await ToolsService.getAssignedTool(toolId);
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        if (mountedRef.current) {
          setToolData(response.data);
          //console.log(`✅ [${contextModule}] Datos de herramienta ${toolId} cargados:`, response.data);
        }
      } else {
        throw new Error(`Error ${response.statusCode}: No se pudo cargar la herramienta`);
      }
    } catch (error) {
      console.error(`❌ [${contextModule}] Error al cargar herramienta ${toolId}:`, error);
      if (mountedRef.current) {
        showMessage('error', 'Error al cargar los datos de la herramienta');
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [toolId, contextModule]);

  // Función helper para mostrar mensajes (usa prop o snackbar interno)
  const showMessage = useCallback((type, message) => {
    if (showSnackbar) {
      showSnackbar(type, message);
    } else {
      setSnackbar({
        open: true,
        type,
        message
      });
    }
  }, [showSnackbar]);

  // Manejar cambios en el formulario de inspección
  const handleInspectionFormChange = (e) => {
    const { name, value } = e.target;
    setInspectionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Crear nueva inspección
  const handleCreateInspection = async (e) => {
    e.preventDefault();
    
    if (submittingInspection) return;

    // Validar formulario
    if (!inspectionForm.type || !inspectionForm.date || !inspectionForm.responsable) {
      showMessage('error', 'Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      setSubmittingInspection(true);
      
      const inspectionData = {
        type: inspectionForm.type,
        date: dayjs(inspectionForm.date).toISOString(),
        responsable: inspectionForm.responsable,
        observations: inspectionForm.observations || '',
        finished: false // ✅ CORREGIDO: Usar finished en lugar de estado
      };

      //console.log(`📝 [${contextModule}] Creando inspección para herramienta ${toolId}:`, inspectionData);
      
      await InspectionsService.createToolInspection(toolId, inspectionData);
      
      showMessage('success', 'Inspección creada exitosamente');

      // Limpiar formulario
      resetInspectionForm();

      // Recargar datos
      await loadToolData();

      // Notificar al componente padre
      if (onToolUpdate) {
        onToolUpdate();
      }

      // ✅ CAMBIADO: No cerrar el modal para permitir más cambios

    } catch (error) {
      console.error(`❌ [${contextModule}] Error al crear inspección:`, error);
      showMessage('error', 'Error al crear la inspección');
    } finally {
      setSubmittingInspection(false);
    }
  };

  // Finalizar inspección
  const handleFinishInspection = async (inspection) => {
    if (window.confirm('¿Está seguro de finalizar esta inspección?')) {
      try {
        //console.log(`✅ [${contextModule}] Finalizando inspección ${inspection.id}`);
        
        // ✅ CORREGIDO: Actualizar el campo finished en lugar de estado
        const updatedInspection = {
          ...inspection,
          finished: true, // Marcar como finalizada
         // date: dayjs().toISOString() // Fecha de finalización
        };

        await InspectionsService.updateInspection(inspection.id, updatedInspection);
        
        showMessage('success', 'Inspección finalizada correctamente');

        // Recargar datos
        await loadToolData();

        // Notificar al componente padre
        if (onToolUpdate) {
          onToolUpdate();
        }

      } catch (error) {
        console.error(`❌ [${contextModule}] Error al finalizar inspección:`, error);
        showMessage('error', 'Error al finalizar la inspección');
      }
    }
  };

  // Función para cerrar snackbar interno
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Función para cerrar modal
  const handleCloseModal = () => {
    setToolData(null);
    setActiveTab('info');
    resetInspectionForm();
    onHide();
  };

  // Función para renderizar información de asignación según el contexto
  const renderAssignmentInfo = () => {
    if (!toolData) return null;

    return (
      <Card>
        <Card.Header>
          <h6 className="mb-0">Asignación Actual</h6>
        </Card.Header>
        <Card.Body>
          {toolData.person && (
            <>
              <Row className="mb-2">
                <Col xs={4}><strong>Persona:</strong></Col>
                <Col xs={8}>
                  <FaUser className="me-1" />
                  {toolData.person.firstName} {toolData.person.surname}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4}><strong>Documento:</strong></Col>
                <Col xs={8}>{toolData.person.document}</Col>
              </Row>
            </>
          )}
          {toolData.work && (
            <Row className="mb-2">
              <Col xs={4}><strong>Obra:</strong></Col>
              <Col xs={8}>
                <FaBriefcase className="me-1" />
                {toolData.work.name}
              </Col>
            </Row>
          )}
          <Row className="mb-2">
            <Col xs={4}><strong>Fecha Asignación:</strong></Col>
            <Col xs={8}>
              <FaCalendarAlt className="me-1" />
              {toolData.createdDate ? dayjs(toolData.createdDate).format(FORMATO_FECHA) : 'N/A'}
            </Col>
          </Row>
          {toolData.deletedDate && (
            <Row className="mb-2">
              <Col xs={4}><strong>Fecha Retiro:</strong></Col>
              <Col xs={8}>
                <FaCalendarAlt className="me-1" />
                {dayjs(toolData.deletedDate).format(FORMATO_FECHA)}
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    );
  };

  // ✅ CORREGIDO: Columnas para la tabla de inspecciones usando el campo finished
// ✅ MEJORADO: Columnas para la tabla de inspecciones con mejor visualización de observaciones
const inspectionColumns = [
  {
    headerName: 'Tipo',
    field: 'type',
    flex: 1,
    valueFormatter: params => getTipoDescripcion(params.value, TIPOS_INSPECCIONES_EQUIPOS),
    filter: 'agTextColumnFilter',
    floatingFilter: true
  },
  {
    headerName: 'Fecha',
    field: 'date',
    flex: 1,
    valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
    filter: 'agTextColumnFilter',
    floatingFilter: true
  },
  {
    headerName: 'Responsable',
    field: 'responsable',
    flex: 1,
    filter: 'agTextColumnFilter',
    floatingFilter: true
  },
  {
    headerName: 'Observaciones',
    field: 'observations',
    flex: 2, // ✅ Más ancho que otras columnas
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    wrapText: true, // ✅ Permite que el texto se ajuste en múltiples líneas
    autoHeight: true, // ✅ La fila se ajusta automáticamente a la altura del contenido
    tooltipField: 'observations', // ✅ Tooltip adicional por si el texto es muy largo
    cellStyle: { 
      lineHeight: '1.5',
      padding: '10px 8px',
      whiteSpace: 'normal' // ✅ Permite saltos de línea
    }
  },
  {
    headerName: 'Estado',
    field: 'finished',
    width: 90,
    cellRenderer: (params) => {
      const finished = params.value;
      
      if (finished) {
        return <Badge bg="success" style={{ fontSize: '0.75rem' }}>Finalizada</Badge>;
      } else {
        return <Badge bg="warning" style={{ fontSize: '0.75rem' }}>Pendiente</Badge>;
      }
    }
  },
  {
    headerName: 'Acciones',
    field: 'id',
    width: 100,
    sortable: false,
    filter: false,
    cellRenderer: (params) => {
      const inspection = params.data;
      
      if (!inspection.finished) {
        return (
          <Button
            variant="outline-success"
            size="xs"
            onClick={() => handleFinishInspection(inspection)}
            className="btn-xs px-2 py-1"
            title="Finalizar inspección"
            style={{ fontSize: '0.75rem', lineHeight: '1.2' }}
          >
            <FaCheckSquare className="me-1" /> Finalizar
          </Button>
        );
      }
      
      return (
        <Badge bg="success" className="text-center w-100" style={{ fontSize: '0.75rem' }}>
          Finalizada
        </Badge>
      );
    }
  }
];

  if (!show) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={handleCloseModal}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaTools className="me-2" />
            {toolData ? 
              `${toolData.tool?.name || 'Herramienta'} - Serial: ${toolData.serial || 'N/A'}` : 
              'Perfil de Herramienta'
            }
            {contextModule !== 'general' && (
              <Badge bg="info" className="ms-2">{contextModule}</Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-2">Cargando datos de la herramienta...</p>
            </div>
          ) : toolData ? (
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              {/* Tab de Información */}
              <Tab eventKey="info" title={
                <span><FaTools className="me-1" />Información</span>
              }>
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">Datos de la Herramienta</h6>
                      </Card.Header>
                      <Card.Body>
                        <Row className="mb-2">
                          <Col xs={4}><strong>Tipo:</strong></Col>
                          <Col xs={8}>{getTipoDescripcion(toolData.tool?.type, TIPOS_EQUIPOS)}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={4}><strong>Nombre:</strong></Col>
                          <Col xs={8}>{toolData.tool?.name}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={4}><strong>Serial:</strong></Col>
                          <Col xs={8}>{toolData.serial || 'N/A'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={4}><strong>Cantidad:</strong></Col>
                          <Col xs={8}>{toolData.quantity || 1}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={4}><strong>Estado:</strong></Col>
                          <Col xs={8}>
                            <Badge bg={toolData.retired ? 'danger' : 'success'}>
                              {toolData.retired ? 'Retirado' : 'Activo'}
                            </Badge>
                          </Col>
                        </Row>
                        {toolData.tool?.id && (
                          <Row className="mb-2">
                            <Col xs={4}><strong>ID Herramienta:</strong></Col>
                            <Col xs={8}><code>{toolData.tool.id}</code></Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    {renderAssignmentInfo()}
                  </Col>
                </Row>
              </Tab>

              {/* Tab de Inspecciones */}
              <Tab eventKey="inspections" title={
                <span>
                  <FaClipboardCheck className="me-1" />
                  Inspecciones
                  {toolData.inspections && toolData.inspections.length > 0 && (
                    <Badge bg="primary" className="ms-1">{toolData.inspections.length}</Badge>
                  )}
                </span>
              }>
                <Row>
                  <Col md={4}>
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">Nueva Inspección</h6>
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleCreateInspection}>
                          <Form.Group className="mb-3">
                            <Form.Label>Tipo de Inspección *</Form.Label>
                            <Form.Select
                              name="type"
                              value={inspectionForm.type}
                              onChange={handleInspectionFormChange}
                              required
                            >
                              <option value="">Seleccione tipo...</option>
                              {TIPOS_INSPECCIONES_EQUIPOS.map(tipo => (
                                <option key={tipo.id} value={tipo.id}>
                                  {tipo.descripcion}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>

 <Form.Group className="mb-3">
                            <Form.Label>Fecha *</Form.Label>
                            <Form.Control
                              type="date"
                              name="date"
                              value={inspectionForm.date}
                              onChange={handleInspectionFormChange}
                              required
                              // ✅ CORREGIDO: Sin restricción de fechas - permite cualquier fecha
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Responsable *</Form.Label>
                            <Form.Control
                              type="text"
                              name="responsable"
                              value={inspectionForm.responsable}
                              onChange={handleInspectionFormChange}
                              placeholder="Nombre del responsable"
                              required
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Observaciones</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="observations"
                              value={inspectionForm.observations}
                              onChange={handleInspectionFormChange}
                              placeholder="Observaciones opcionales..."
                            />
                          </Form.Group>

                          <Button
                            type="submit"
                            variant="primary"
                            disabled={submittingInspection}
                            className="w-100"
                          >
                            {submittingInspection ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <FaPlus className="me-1" />
                                Crear Inspección
                              </>
                            )}
                          </Button>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={8}>
                    <Card>
                      <Card.Header>
                        <h6 className="mb-0">Lista de Inspecciones</h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {toolData.inspections && toolData.inspections.length > 0 ? (
                          <TableWrapper
                            columnDefs={inspectionColumns}
                            rowData={toolData.inspections}
                            tableType="inspecciones"
                            customGridOptions={{
                              pagination: true,
                              paginationPageSize: 10,
                              domLayout: 'autoHeight'
                            }}
                          />
                        ) : (
                          <Alert variant="info" className="m-3">
                            <FaClipboardCheck className="me-2" />
                            No hay inspecciones registradas para esta herramienta.
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Tab de Historial */}
              <Tab eventKey="history" title={
                <span>
                  <FaHistory className="me-1" />
                  Historial
                  {toolData.history && toolData.history.length > 0 && (
                    <Badge bg="secondary" className="ms-1">{toolData.history.length}</Badge>
                  )}
                </span>
              }>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Historial de Asignaciones</h6>
                  </Card.Header>
                  <Card.Body>
                    {toolData.history && toolData.history.length > 0 ? (
                      <ListGroup variant="flush">
                        {toolData.history.map((item, index) => (
                          <ListGroup.Item key={index} className="d-flex justify-content-between align-items-start">
                            <div className="ms-2 me-auto">
                              <div className="fw-bold">
                                {item.person ? (
                                  <>
                                    <FaUser className="me-1" />
                                    {item.person.firstName} {item.person.surname}
                                    <small className="text-muted ms-2">({item.person.document})</small>
                                  </>
                                ) : item.work ? (
                                  <>
                                    <FaBriefcase className="me-1" />
                                    {item.work.name}
                                  </>
                                ) : (
                                  'Sin asignación específica'
                                )}
                              </div>
                              <small>
                                <FaCalendarAlt className="me-1" />
                                Desde: {item.createdAt ? dayjs(item.createdAt).format(FORMATO_FECHA) : 'N/A'}
                                {item.deletedDate && (
                                  <> - Hasta: {dayjs(item.deletedDate).format(FORMATO_FECHA)}</>
                                )}
                              </small>
                            </div>
                            <Badge bg={index === toolData.history.length - 1 ? 'success' : 'secondary'} pill>
                            {index === toolData.history.length - 1 ? 'Activo' : 'Reasignado'}
                            </Badge>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <Alert variant="info">
                        <FaHistory className="me-2" />
                        No hay historial disponible para esta herramienta.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          ) : (
            <Alert variant="warning">
              <FaTools className="me-2" />
              No se pudieron cargar los datos de la herramienta.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <FaTimes className="me-1" /> Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Snackbar interno (solo se usa si no se proporciona showSnackbar) */}
      {!showSnackbar && (
        <CustomSnackbar
          open={snackbar.open}
          type={snackbar.type}
          message={snackbar.message}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default ToolProfileModal;